import { useId, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ROUTES } from '@/app/router/routes';
import { EmptyState } from '@/components/feedback/EmptyState';
import {
  AlertCircleIcon,
  BriefcaseIcon,
  CalendarIcon,
  CheckIcon,
  ChevronRightIcon,
  ClipboardCheckIcon,
  EditIcon,
  FileTextIcon,
  SpinnerIcon,
  TimesheetIcon,
} from '@/components/icons';
import { Avatar } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { buttonClasses } from '@/components/ui/button-styles';
import { TabPanel, Tabs } from '@/components/ui/Tabs';
import { TimesheetSummaryPanel } from './components/TimesheetSummaryPanel';
import { TimesheetMonthGrid } from './components/TimesheetMonthGrid';
import { TimesheetInformation } from './components/TimesheetInformation';
import { TimesheetExtras } from './components/TimesheetExtras';
import {
  useReplaceDays,
  useReplaceOvertimes,
  useTimesheet,
  useTimesheetOptions,
} from './useTimesheets';
import { formatMonth, STATE_TONES } from './timesheet-labels';
import type { TimesheetDayInput, TimesheetOvertimeInput } from './types';
import './Timesheets.css';
import './TimesheetDetail.css';

type DetailTab = 'activity' | 'absence' | 'overtime' | 'information' | 'other';

export const TimesheetPage = () => {
  const { id } = useParams<{ id: string }>();
  return <TimesheetDetail key={id} />;
};

const TimesheetDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [editing, setEditing] = useState(false);
  const navigate = useNavigate();
  const [workModelId, setWorkModelId] = useState('');
  const [tab, setTab] = useState<DetailTab>('activity');
  const tabsId = useId();
  const [calendarBusy, setCalendarBusy] = useState(false);
  const query = useTimesheet(id);
  const options = useTimesheetOptions();
  const replaceDays = useReplaceDays();
  const replaceOvertimes = useReplaceOvertimes();

  if (query.isPending)
    return (
      <p role="status" className="cra-loading">
        <SpinnerIcon /> Chargement du compte rendu…
      </p>
    );
  if (query.isError || !query.data)
    return (
      <EmptyState
        icon={<TimesheetIcon />}
        title="Compte rendu indisponible"
        description="Vérifiez votre connexion et votre accès à ce compte rendu."
        action={
          <div className="cra-actions">
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                void query.refetch();
              }}
            >
              Réessayer
            </Button>
            <Link to={ROUTES.timesheets} className={buttonClasses({ size: 'sm' })}>
              Revenir à mes CRA
            </Link>
          </div>
        }
      />
    );

  const timesheet = query.data;
  const isEditing = editing && timesheet.permissions.canEdit;
  const pending = replaceDays.isPending || replaceOvertimes.isPending;
  const error = replaceDays.error ?? replaceOvertimes.error;
  const saveDays = async (days: TimesheetDayInput[]) => {
    if (!isEditing) throw new Error('Cliquez sur Modifier pour modifier ce CRA.');
    await replaceDays.mutateAsync({ id: timesheet.id, days });
  };
  const saveOvertimes = async (overtimes: TimesheetOvertimeInput[]) => {
    if (!isEditing) throw new Error('Cliquez sur Modifier pour modifier ce CRA.');
    await replaceOvertimes.mutateAsync({ id: timesheet.id, overtimes });
  };

  const reviewActions = (
    <div className="cra-actions">
      {timesheet.permissions.canSubmit && (
        <Button
          size="sm"
          disabled={pending || calendarBusy}
          onClick={() => void navigate(ROUTES.timesheetSatisfaction(timesheet.id))}
        >
          <ClipboardCheckIcon />
          Envoyer mon CRA
        </Button>
      )}
    </div>
  );

  return (
    <div className="cra-page cra-detail-page">
      <nav className="cra-breadcrumb" aria-label="Fil d’Ariane">
        <Link to={ROUTES.timesheets}>Mes CRA</Link>
        <ChevronRightIcon aria-hidden />
        <span aria-current="page">
          {timesheet.mission.consultantName} · {formatMonth(timesheet.periodMonth)}
        </span>
      </nav>
      <section className="cra-surface cra-identity cra-detail-heading" aria-label="Compte rendu">
        <div className="cra-identity__top">
          <div className="cra-identity__person">
            <Avatar
              name={timesheet.mission.consultantName}
              src={timesheet.mission.consultantAvatarUrl}
              className="size-14"
            />
            <div>
              <span className="cra-eyebrow">Compte rendu d’activité</span>
              <h1>{timesheet.mission.consultantName}</h1>
              <p className="cra-detail-mission">
                {timesheet.mission.title || timesheet.mission.projectName}
                {timesheet.mission.clientName ? ` · ${timesheet.mission.clientName}` : ''}
              </p>
              <Link
                to={ROUTES.mission(timesheet.mission.id)}
                className="text-accent-700 text-xs underline"
              >
                Voir la mission · du{' '}
                {new Date(`${timesheet.mission.startDate}T12:00:00`).toLocaleDateString('fr-FR')}
                {timesheet.mission.endDate
                  ? ` au ${new Date(`${timesheet.mission.endDate}T12:00:00`).toLocaleDateString('fr-FR')}`
                  : ''}
              </Link>
            </div>
          </div>
          <div className="cra-identity__period">
            <TimesheetIcon aria-hidden />
            <span>{formatMonth(timesheet.periodMonth)}</span>
            <Badge tone={STATE_TONES[timesheet.state.code] ?? 'neutral'}>
              {timesheet.state.label}
            </Badge>
            {timesheet.permissions.canEdit && (
              <Button
                size="sm"
                variant={isEditing ? 'primary' : 'secondary'}
                disabled={pending || calendarBusy}
                onClick={() => setEditing(!isEditing)}
              >
                {isEditing ? <CheckIcon /> : <EditIcon />}
                {isEditing ? 'Terminer la modification' : 'Modifier'}
              </Button>
            )}
          </div>
        </div>
      </section>
      {timesheet.state.code === 'REJETE' && timesheet.rejectionReason && (
        <div className="cra-notice">
          <AlertCircleIcon aria-hidden />
          <div>
            <strong>Renvoyé pour correction</strong>
            <p>{timesheet.rejectionReason}</p>
          </div>
        </div>
      )}
      {error && (
        <div role="alert" className="cra-notice">
          <AlertCircleIcon aria-hidden />
          {error.message}
        </div>
      )}
      <div className="cra-detail-workspace">
        <div className="cra-detail-main">
          <div className="cra-detail-navigation">
            <Tabs
              id={tabsId}
              value={tab}
              onChange={setTab}
              size="sm"
              label="Détail du CRA"
              tabs={[
                { value: 'activity', label: 'Calendrier', icon: <TimesheetIcon /> },
                { value: 'absence', label: 'Absences', icon: <CalendarIcon /> },
                { value: 'overtime', label: 'Heures supplémentaires', icon: <TimesheetIcon /> },
                { value: 'information', label: 'Informations et suivi', icon: <FileTextIcon /> },
                { value: 'other', label: 'Autres déclarations', icon: <BriefcaseIcon /> },
              ]}
            />
          </div>
          <div className="cra-detail-content">
            {tab !== 'information' && options.isPending && (
              <p role="status" className="cra-loading">
                <SpinnerIcon />
                Chargement des options de saisie…
              </p>
            )}
            {tab !== 'information' && options.isError && (
              <div role="alert" className="cra-notice">
                Les options de saisie sont indisponibles.
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    void options.refetch();
                  }}
                >
                  Réessayer
                </Button>
              </div>
            )}
            {/* Les panneaux restent montés : changer d’onglet ne perd ni le mode
                choisi ni une sauvegarde de journée en attente de double clic. */}
            <TabPanel
              id={`${tabsId}-activity-panel`}
              labelledBy={`${tabsId}-activity`}
              hidden={tab !== 'activity'}
            >
              <section className="cra-detail-calendar" aria-label="Activité du mois">
                <div className="cra-detail-calendar__heading">
                  <div>
                    <h2>{formatMonth(timesheet.periodMonth)}</h2>
                    <span role="status" className="cra-save-status">
                      {!isEditing
                        ? 'Lecture seule'
                        : replaceDays.isPending || calendarBusy
                          ? 'Enregistrement…'
                          : replaceDays.isSuccess
                            ? 'Modifications enregistrées'
                            : 'Sauvegarde automatique'}
                    </span>
                  </div>
                  {options.data && isEditing && options.data.workModels.length > 0 && (
                    <fieldset className="cra-work-model" disabled={pending || calendarBusy}>
                      <legend>Mode des nouvelles journées</legend>
                      <div className="cra-work-model__choices">
                        {[{ id: '', label: 'Non précisé' }, ...options.data.workModels].map(
                          (model) => (
                            <label key={model.id}>
                              <input
                                type="radio"
                                name="cra-work-model"
                                value={model.id}
                                checked={workModelId === model.id}
                                onChange={() => setWorkModelId(model.id)}
                              />
                              <span>
                                <CheckIcon />
                                {model.label}
                              </span>
                            </label>
                          ),
                        )}
                      </div>
                    </fieldset>
                  )}
                </div>
                {options.data && (
                  <>
                    <TimesheetMonthGrid
                      key={`${timesheet.id}-${isEditing}`}
                      readOnly={!isEditing}
                      timesheet={timesheet}
                      dayTypes={options.data.dayTypes}
                      workModels={options.data.workModels}
                      holidays={[
                        ...new Set([
                          ...(timesheet.holidays ?? []),
                          ...timesheet.days
                            .filter((day) => day.dayType.code === 'FERIE')
                            .map((day) => day.date),
                        ]),
                      ]}
                      pending={pending}
                      defaultWorkModelId={workModelId}
                      error={replaceDays.error?.message}
                      onChange={saveDays}
                      onInteractionBusyChange={setCalendarBusy}
                    />
                  </>
                )}
              </section>
            </TabPanel>
            {(['absence', 'overtime', 'other'] as const).map((view) => (
              <TabPanel
                key={view}
                id={`${tabsId}-${view}-panel`}
                labelledBy={`${tabsId}-${view}`}
                hidden={tab !== view}
              >
                {options.data && (
                  <TimesheetExtras
                    key={`${timesheet.id}-${view}-${isEditing}`}
                    readOnly={!isEditing}
                    timesheet={timesheet}
                    options={options.data}
                    pending={pending || calendarBusy}
                    onOvertimesChange={saveOvertimes}
                    view={view}
                    isActive={tab === view}
                  />
                )}
              </TabPanel>
            ))}
            <TabPanel
              id={`${tabsId}-information-panel`}
              labelledBy={`${tabsId}-information`}
              hidden={tab !== 'information'}
            >
              <TimesheetInformation timesheet={timesheet} />
            </TabPanel>
          </div>
        </div>
        <TimesheetSummaryPanel
          timesheet={timesheet}
          dayTypes={options.data?.dayTypes ?? []}
          actions={reviewActions}
          pending={pending || calendarBusy}
        />
      </div>
    </div>
  );
};
